import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Popover, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { MutableRefObject, useEffect, useState } from 'react';
import CategoryAutoComplete from '@src/components/auto-complete/CategoryAutoComplete';
import { useCreateInventoryLazy } from '@src/adapters/hooks/inventory/useCreateInventory';
import InventoryCreateDTO from '@src/adapters/dto/inventory/InventoryCreateDTO';
import InventoryDTO from '@src/adapters/dto/inventory/InventoryDTO';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';
import ManufacturerAutoComplete from '@src/components/auto-complete/ManufacturerAutoComplete';

interface Props {
  itemName: string;
  open: boolean;
  close(inventory: InventoryDTO | undefined): void;
  anchorEl?: MutableRefObject<null> | null;
}

function InventoryAdd({ close, itemName, open, anchorEl }: Props) {
  const { execute: create } = useCreateInventoryLazy();

  const { control, reset, setValue, getValues } = useForm<InventoryCreateDTO>();

  const handleSubmit = async () => {
    const result = await create({
      inventory: getValues(),
    });

    if (result.inventory) {
      reset();
      close(result.inventory);
    }
  };

  const handleClose = () => {
    reset();
    close(undefined);
  };

  useEffect(() => {
    setValue('name', itemName);
  }, [itemName, setValue]);

  const handleManufacturerChanged = (item: IdNameDto) => {
    const manufacturer = getValues('manufacturer');
    manufacturer.id = item.id;
    manufacturer.name = item.name;
    setValue('manufacturer', manufacturer);
  };

  return (
    <Popover
      open={open}
      aria-labelledby="Inventory Item"
      aria-describedby="Inventory Item"
      disableEnforceFocus
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      anchorEl={anchorEl ? anchorEl?.current : null}
      sx={{
        '& .MuiPaper-root': {
          width: '25%',
          padding: (theme) => theme.spacing(2),
        },
      }}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography variant="h6">Add Inventory Item</Typography>
      </DialogTitle>
      <form>
        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}>
          <Typography variant="subtitle1">Global Properties</Typography>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Item name"
                sx={{ width: '100%', marginTop: '20px', marginBottom: '20px' }}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
          <CategoryAutoComplete
            control={control}
            name="category"
            isRequired={true}
          />
          <Typography variant="subtitle1">Manufacturer Properties</Typography>
          <ManufacturerAutoComplete update={handleManufacturerChanged} />
          <Controller
            name="manufacturer.productCode"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Product code"
                sx={{ width: '100%', marginTop: '20px', marginBottom: '20px' }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
          <Button
            onClick={() => handleClose()}
            color="primary"
            variant="outlined"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit()}
            color="primary"
            variant="contained"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Add
          </Button>
        </DialogActions>
      </form>
    </Popover>
  );
}

export default InventoryAdd;
